import { useEffect, useMemo, useState } from "react";
import moment from "moment";

// Day + time-slot picker for booking a lesson.
// Contract: the chosen slot is written to localStorage as 'event'
// ({start, end}) — LessonModal reads it on checkout.
const ScheduleSlot = ({ schedule, onSelect }) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const slots = useMemo(() => {
        const now = new Date();
        return (schedule?.flat() || [])
            .map((s) => ({ start: new Date(s.start_time), end: new Date(s.end_time) }))
            .filter((s) => !isNaN(s.start.getTime()) && s.start > now)
            .sort((a, b) => a.start - b.start);
    }, [schedule]);

    const days = useMemo(() => {
        const map = new Map();
        slots.forEach((s) => {
            const key = moment(s.start).format("YYYY-MM-DD");
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(s);
        });
        return Array.from(map.entries()).map(([key, daySlots]) => ({
            key,
            date: daySlots[0].start,
            slots: daySlots,
        }));
    }, [slots]);

    const [activeDay, setActiveDay] = useState(null);
    const [selected, setSelected] = useState(null);

    // restore a previous selection only if it is one of this course's slots,
    // otherwise drop the stale value so checkout can't use it
    useEffect(() => {
        let stored = null;
        try {
            stored = JSON.parse(localStorage.getItem("event"));
        } catch (e) { /* ignore */ }
        const match = stored?.start
            ? slots.find((s) => s.start.getTime() === new Date(stored.start).getTime())
            : null;
        if (match) {
            setSelected(match);
        } else {
            setSelected(null);
            localStorage.removeItem("event");
        }
    }, [slots]);

    // default day: the selected slot's day, else today if available, else first upcoming
    useEffect(() => {
        if (!days.length) return;
        const selectedKey = selected ? moment(selected.start).format("YYYY-MM-DD") : null;
        if (selectedKey && days.some((d) => d.key === selectedKey)) {
            setActiveDay(selectedKey);
            return;
        }
        const todayKey = moment().format("YYYY-MM-DD");
        setActiveDay(days.some((d) => d.key === todayKey) ? todayKey : days[0].key);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days, selected == null]);

    const pick = (slot) => {
        setSelected(slot);
        localStorage.setItem("event", JSON.stringify({ start: slot.start, end: slot.end }));
        if (onSelect) onSelect(slot);
    };

    if (!days.length) {
        return (
            <div className="schedule-picker">
                <div className="sp-empty">
                    <i className="fa-light fa-calendar-xmark d-block mb-2" style={{ fontSize: 28 }} />
                    No available time slots right now.
                    <br />
                    Please check back later.
                </div>
            </div>
        );
    }

    const activeSlots = days.find((d) => d.key === activeDay)?.slots || [];
    const isSelected = (s) => selected && s.start.getTime() === selected.start.getTime();

    return (
        <div className="schedule-picker">
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                <h6 className="sp-heading mb-0">Pick a date</h6>
                <span className="sp-note">
                    <i className="fa-light fa-globe me-1" />
                    Times shown in {timezone}
                </span>
            </div>

            <div className="sp-days">
                {days.map((d) => (
                    <button
                        type="button"
                        key={d.key}
                        className={`sp-day ${activeDay === d.key ? "active" : ""}`}
                        onClick={() => setActiveDay(d.key)}
                    >
                        <span className="sp-dow">{moment(d.date).format("ddd")}</span>
                        <span className="sp-date">{moment(d.date).format("DD")}</span>
                        <span className="sp-month">{moment(d.date).format("MMM")}</span>
                        <span className="sp-count">{d.slots.length} slot{d.slots.length > 1 ? "s" : ""}</span>
                    </button>
                ))}
            </div>

            <h6 className="sp-heading mt-4 mb-2">
                Available times — {moment(activeDay, "YYYY-MM-DD").format("dddd, MMM D")}
                {activeDay === moment().format("YYYY-MM-DD") ? " (Today)" : ""}
            </h6>
            <div className="sp-times">
                {activeSlots.map((s, i) => (
                    <button
                        type="button"
                        key={i}
                        className={`sp-time ${isSelected(s) ? "selected" : ""}`}
                        onClick={() => pick(s)}
                    >
                        {isSelected(s) && <i className="fa-solid fa-circle-check me-2" />}
                        {moment(s.start).format("h:mm A")} – {moment(s.end).format("h:mm A")}
                    </button>
                ))}
            </div>

            {selected && (
                <div className="sp-summary">
                    <i className="fa-light fa-calendar-check" />
                    <span>
                        Selected: <b>{moment(selected.start).format("dddd, MMM D")}</b> ·{" "}
                        <b>{moment(selected.start).format("h:mm A")} – {moment(selected.end).format("h:mm A")}</b>
                    </span>
                </div>
            )}
        </div>
    );
};

export default ScheduleSlot;
